/**
 * Contact WarCarp — /(app)/settings/contact
 *
 * In-app feedback form. Saves a ticket and notifies the admin app.
 * Replies appear in the player's Contact WarCarp.
 */

import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React, { useState } from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useOrientation } from '../../../hooks/useOrientation'
import { useTheme } from '../../../hooks/useTheme'
import { useContactSubmit } from '../../../hooks/useContact'
import { KeyboardAwareScrollView } from '../../../components/ui/KeyboardAwareScrollView'
import { Button } from '../../../components/ui/Button'
import { collectDeviceInfo } from '../../../lib/deviceInfo'
import type { ContactRequest } from '@warcarp/api-types'
import { goBackOr } from '../../../lib/goBack'

type Reason = ContactRequest['reason']

const REASONS: Array<{ value: Reason; label: string; hint: string }> = [
  { value: 'feedback', label: 'General feedback', hint: "Anything you'd like to share." },
  { value: 'feature', label: 'Feature request', hint: 'An idea or improvement you want to see.' },
  { value: 'bug', label: 'Bug report', hint: 'Something is broken or behaves wrong.' },
]

const MAX_MESSAGE_LENGTH = 2000

export default function ContactScreen() {
  const router = useRouter()
  const { reason: requestedReason } = useLocalSearchParams<{ reason?: string | string[] }>()
  const { theme } = useTheme()
  // TextInput placeholder colour can't read CSS variables — pin it.
  const placeholderHex = theme === 'dark' ? '#8a8c6e' : '#6b5b3e'
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(560, winWidth * 0.6) : undefined

  const initialReason = Array.isArray(requestedReason) ? requestedReason[0] : requestedReason
  const [reason, setReason] = useState<Reason>(
    initialReason === 'bug' || initialReason === 'feature' || initialReason === 'feedback'
      ? initialReason
      : 'feedback',
  )
  const [message, setMessage] = useState('')
  const [sent, setSent] = useState(false)
  const submit = useContactSubmit()

  const trimmed = message.trim()
  const canSubmit = trimmed.length > 0 && !submit.isPending && !sent

  async function handleSubmit() {
    if (!canSubmit) return
    try {
      // Attach best-effort device info so the maintainer can triage without a
      // follow-up (platform, OS/app version, device/user-agent).
      await submit.mutateAsync({ reason, message: trimmed, deviceInfo: collectDeviceInfo() })
      setSent(true)
    } catch {
      // Error is surfaced inline via submit.isError below.
    }
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas" edges={['top']}>
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings/help')}
          accessibilityLabel="Back"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">Contact WarCarp</Text>
      </View>

      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: 16,
          gap: 16,
          paddingBottom: 32,
          alignItems: isLandscape ? 'center' : undefined,
        }}
      >
          <View style={isLandscape ? { width: contentWidth, gap: 16 } : { width: '100%', gap: 16 }}>
          {sent ? (
            <View className="bg-paper border-2 border-olive rounded-xl p-4 gap-3 items-center">
              <Text className="font-stencil text-ink text-base uppercase tracking-widest">
                Thanks — sent.
              </Text>
              <Text className="font-typed text-ink-soft text-sm text-center">
                WarCarp support may follow-up with you here.
              </Text>
              <View className="w-full gap-3">
                <Button title="View Report" onPress={() => router.push('/(app)/settings/inbox')} />
                <Button title="Back to Lobby" variant="secondary" onPress={() => router.replace('/(app)')} />
              </View>
            </View>
          ) : (
            <>
              {/* Reason picker */}
              <View className="gap-2">
                <Text className="font-stencil text-[10px] uppercase tracking-widest text-olive-up">
                  What's this about?
                </Text>
                <View className="gap-2">
                  {REASONS.map((r) => {
                    const selected = reason === r.value
                    return (
                      <TouchableOpacity
                        key={r.value}
                        onPress={() => setReason(r.value)}
                        accessibilityLabel={r.label}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: selected }}
                        aria-checked={selected}
                        className={`rounded-xl px-4 py-3 border-2 ${
                          selected ? 'bg-blood border-ink' : 'bg-paper border-olive'
                        }`}
                        style={{ minHeight: 56 }}
                      >
                        <Text
                          className={`font-stencil uppercase text-sm ${
                            selected ? 'text-paper' : 'text-ink'
                          }`}
                        >
                          {r.label}
                        </Text>
                        <Text
                          className={`font-typed text-[11px] italic mt-0.5 ${
                            selected ? 'text-paper/80' : 'text-ink-soft'
                          }`}
                        >
                          {r.hint}
                        </Text>
                      </TouchableOpacity>
                    )
                  })}
                </View>
              </View>

              {/* Message */}
              <View className="gap-2">
                <View className="flex-row items-baseline justify-between">
                  <Text className="font-stencil text-[10px] uppercase tracking-widest text-olive-up">
                    Message
                  </Text>
                  <Text className="font-typed text-[10px] text-ink-soft">
                    {message.length}/{MAX_MESSAGE_LENGTH}
                  </Text>
                </View>
                <TextInput
                  value={message}
                  onChangeText={(t) => setMessage(t.slice(0, MAX_MESSAGE_LENGTH))}
                  placeholder="Tell us what's on your mind…"
                  placeholderTextColor={placeholderHex}
                  multiline
                  textAlignVertical="top"
                  accessibilityLabel="Your message"
                  className="bg-paper border-2 border-olive rounded-xl px-3 py-3 text-ink font-typed text-base"
                  style={{ minHeight: 160 }}
                />
              </View>

              {submit.isError && (
                <Text className="font-typed text-blood text-sm">
                  Couldn't send — please try again.
                </Text>
              )}

              <TouchableOpacity
                onPress={handleSubmit}
                disabled={!canSubmit}
                accessibilityLabel="Send message"
                accessibilityRole="button"
                className="bg-blood rounded-xl px-4 py-3 items-center justify-center flex-row gap-2"
                style={{ minHeight: 48, opacity: canSubmit ? 1 : 0.4 }}
              >
                {submit.isPending && <ActivityIndicator size="small" color="white" />}
                <Text className="text-white font-stencil uppercase">Send</Text>
              </TouchableOpacity>

              <Text className="font-typed text-ink-soft text-[11px] italic text-center">
                Your account name, email, and basic device info (platform,
                version) are attached automatically so we can reply and debug.
              </Text>
            </>
          )}
          </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}
